import { useCallback, useMemo, useState } from 'react';

export interface JobFileSelection {
  ids: ReadonlySet<string>;
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  replace: (ids: Iterable<string>) => void;
  clear: () => void;
}

/**
 * The files ticked in the file area.
 *
 * Scoped to what is on screen. `scopeKey` identifies the visible list — the open
 * folder and the active search — and the set empties whenever it changes,
 * because a selection that outlives its list can hold files the user can no
 * longer see, and moving files you cannot see is not something the UI should
 * make possible.
 *
 * The reset happens during render rather than in an effect: an effect would let
 * one paint through with the stale selection still ticked.
 */
export function useJobFileSelection(scopeKey: string): JobFileSelection {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  const [scope, setScope] = useState(scopeKey);

  if (scope !== scopeKey) {
    setScope(scopeKey);
    setIds(new Set());
  }

  const has = useCallback((id: string) => ids.has(id), [ids]);

  const toggle = useCallback((id: string) => {
    setIds((current) => {
      const next = new Set(current);
      // delete reports whether it removed anything, which is the same question
      // as "was it selected".
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const replace = useCallback((next: Iterable<string>) => setIds(new Set(next)), []);

  const clear = useCallback(() => setIds(new Set()), []);

  return useMemo(() => ({ ids, has, toggle, replace, clear }), [ids, has, toggle, replace, clear]);
}
